import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { I18NextPipe } from 'angular-i18next';
import { of } from 'rxjs';
import { catchError, concatMap, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import { ErrorMessage, InfoMessage, Message, WarningMessage } from 'src/app/common/messages';
import { State } from 'src/app/dashboards/state';
import { messageType } from 'src/app/enums';
import { DashboardService } from 'src/app/services/services/dashboard.service';
import * as mainActions from 'src/app/state/app.actions';
import { DialogExportReportToDashboardTypes } from 'src/app/reports/reports-details/nested-components/reports-dialog-export/reports-dialog-export.component';
import { DataTransferService } from '../../services/services/data-transfer.service';
import * as actions from './actions';

const saveMessage:string = 'messages:Click "save" to save the changes or "cancel" to discard changes.';
@Injectable()
export class DashboardsEffects {
  constructor(private actions$: Actions,
              private DashboardsService: DashboardService,
              private dataTransferService: DataTransferService,
              private store$: Store<State>,
              private i18nextPipe: I18NextPipe) { }

    loadDashboards$ = createEffect(() => {
        return this.actions$
            .pipe(
                ofType(actions.loadDashboards),
                mergeMap(() => this.DashboardsService.getDashboards()
                    .pipe(
                        map(dashboards => actions.loadDashboardSuccess({ dashboards })),
                        catchError(error => of(actions.loadFailure({ error })))
                    )
                )
            );
    });

    loadDashboardSuccess$ = createEffect(() => {
      return this.actions$
        .pipe(
          ofType(actions.loadDashboardSuccess),
          mergeMap(() => {
            let data = this.dataTransferService.ExportedDashboardData;
            this.dataTransferService.ExportedDashboardData = null;

            return data == null ? [] :
              data.type == DialogExportReportToDashboardTypes.AddToExisting && !data.visible ?
                [mainActions.addMessage({ message: new ErrorMessage(this.i18nextPipe.transform('messages:Exported dashboard is not visible'), this.i18nextPipe.transform("messages:Select dashboard to see it"))})] :
                [actions.handleExportedDashboard({ exportedDashboardData: data })];
          }));
    });

    loadDashboardsConfig$ = createEffect(() => {
        return this.actions$
            .pipe(
                ofType(actions.loadDashboardConfig),
                mergeMap(() => this.DashboardsService.getConfiguration()
                    .pipe(
                        map(config => actions.loadDashboardConfigSuccess({ config })),
                        catchError(error => of(actions.loadFailure({ error })))
                    )
                )
            );
    });
    
    loadDashboardbyId$ = createEffect(() => {
        return this.actions$
            .pipe(
                ofType(actions.loadDashboardById),
                withLatestFrom(this.store$),
                mergeMap(([a,store]) => this.DashboardsService.getDashboardsById(store.dashboard.dashboardIdsForLoad)
                    .pipe(
                        map(dashboards => actions.loadDashboardByIdSuccess({ dashboards })),
                        catchError(error => of(actions.loadFailure({ error })))
                    )
                )
            );
    });

    saveDashboards$ = createEffect(() => {
        return this.actions$
            .pipe(
                ofType(actions.saveDashboards),
                withLatestFrom(this.store$),
                mergeMap(([a, store]) => this.DashboardsService.updateDashboards(store.dashboard.dashboards, store.mainState.userInfo)
                      .pipe(
                          map(dashboards => actions.saveDashboardSuccess({ dashboards })) ,
                          catchError(error => of(actions.loadFailure({ error })))
                      )
                  )
        );
    });

    addDashboards$ = createEffect(() => {
      return this.actions$
        .pipe(
          ofType(actions.addDashboard),
          concatMap((d) => [
            mainActions.addMessage({ message: new InfoMessage(this.i18nextPipe.transform('messages:Dashboard created!'), this.i18nextPipe.transform(saveMessage)) }),
          ])
        );
    });

    deleteDashboards$ = createEffect(() => {
      return this.actions$
        .pipe(
          ofType(actions.removeDashboard),
          concatMap((d) => [
            mainActions.addMessage({ message: new WarningMessage(this.i18nextPipe.transform('messages:Dashboard deleted!'), this.i18nextPipe.transform(saveMessage)) }),
          ])
        );
    });

    updateDashboardAccess$ = createEffect(() => {
      return this.actions$
        .pipe(
          ofType(actions.updateDashboardAccess),
          concatMap((d) => [
            mainActions.addMessage({ message: new InfoMessage(this.i18nextPipe.transform('messages:Visibility permissions updated.'), this.i18nextPipe.transform(saveMessage)) }),
          ])
        );
    });

    // updateDashboardOwner$ = createEffect(() => {
    //     return this.actions$
    //       .pipe(
    //         ofType(actions.updateDashboardOwner),
    //         concatMap((d) => [
    //           mainActions.addMessage({ message: new InfoMessage(this.i18nextPipe.transform('messages:Transfered dashboard owner name.'), this.i18nextPipe.transform(saveMessage)) }),
    //         ])
    //       );
    //   });

    updateDashboardAllowNonOwnersToEdit$ = createEffect(() => {
      return this.actions$
        .pipe(
          ofType(actions.updateDashboardAllowNonOwnersToEdit),
          concatMap((d) =>
            // if dashboard is private, means that action to change AllowNonOwnersToEdit to its default value took place automatically after access was set to "Private"
            // so no need of second message to be deisplayed after the one shown by updateDashboardAccess$ effect (that changes access to "Private")
            d.dashboard.isPublic ?
              [
                mainActions.addMessage({ message: new InfoMessage(this.i18nextPipe.transform('messages:Visibility permissions updated.'), this.i18nextPipe.transform(saveMessage)) }),
              ] : [])
        );
    });

    addWidget$ = createEffect(() => {
        return this.actions$
            .pipe(
                ofType(actions.addWidget),
                concatMap((d) => [
                    mainActions.addMessage({message:new InfoMessage (this.i18nextPipe.transform('messages:Widget created!'),this.i18nextPipe.transform(saveMessage))}),
                ])
            );
    });

    deleteWidget$ = createEffect(() => {
        return this.actions$
            .pipe(
                ofType(actions.removeWidget),
                concatMap((w) => [
                    mainActions.addMessage({message:new WarningMessage (this.i18nextPipe.transform('messages:Widget deleted!'),this.i18nextPipe.transform(saveMessage))}),
                ])
            );
    });        

    saveDashboardsSuccess$ = createEffect(() => {
        return this.actions$
            .pipe(
                ofType(actions.saveDashboardSuccess),
                concatMap(() => [
                    mainActions.addMessage({message:new Message (this.i18nextPipe.transform('messages:Changes saved successfully.')  ,""  ,  messageType.Info )})
                ])
            );
    });

    updateDashboardSettings$ = createEffect(() => {
        return this.actions$
            .pipe(
                ofType(actions.updateDashboardSettings),
                concatMap(() => [
                    mainActions.addMessage({ message: new Message(this.i18nextPipe.transform('messages:Dashboards View Updated.'), this.i18nextPipe.transform(saveMessage), messageType.Info) })
                ])
            );
    });

    ConnectionFail$ = createEffect(() => {
        return this.actions$
            .pipe(
                ofType(actions.loadFailure),
                concatMap(() => [
                    mainActions.addMessage({message:new ErrorMessage(this.i18nextPipe.transform('messages:The connection to the server failed')  ,"")})
                ])
            );
    }); 
}
